import CryptoJS from 'crypto-js';
import { JSEncrypt } from 'jsencrypt';
import { gcm } from '@noble/ciphers/aes.js';

// ==================== 工具函数：ArrayBuffer ↔ WordArray ====================

function arrayBufferToWordArray(buffer) {
  const u8 = new Uint8Array(buffer);
  const words = [];
  for (let i = 0; i < u8.length; i += 4) {
    words.push(
      (u8[i] << 24) |
      ((u8[i + 1] || 0) << 16) |
      ((u8[i + 2] || 0) << 8) |
      (u8[i + 3] || 0)
    );
  }
  return CryptoJS.lib.WordArray.create(words, u8.length);
}

function wordArrayToArrayBuffer(wordArray) {
  const words = wordArray.words;
  const sigBytes = wordArray.sigBytes;
  const u8 = new Uint8Array(sigBytes);
  for (let i = 0; i < sigBytes; i++) {
    u8[i] = (words[i >>> 2] >>> (24 - (i % 4) * 8)) & 0xff;
  }
  return u8.buffer;
}

// ==================== ArrayBuffer 与 Base64 互转 ====================

function arrayBufferToBase64(buffer) {
  const wordArray = arrayBufferToWordArray(buffer);
  return wordArray.toString(CryptoJS.enc.Base64);
}

function base64ToArrayBuffer(base64) {
  const wordArray = CryptoJS.enc.Base64.parse(base64);
  return wordArrayToArrayBuffer(wordArray);
}

// ==================== 字符串与 ArrayBuffer 互转 ====================

function strToArrayBuffer(str) {
  const wordArray = CryptoJS.enc.Utf8.parse(str);
  return wordArrayToArrayBuffer(wordArray);
}

function arrayBufferToStr(buffer) {
  const wordArray = arrayBufferToWordArray(buffer);
  return CryptoJS.enc.Utf8.stringify(wordArray);
}

// ==================== RSA-OAEP 加密（jsencrypt 实现） ====================

/**
 * RSA-OAEP 加密
 * @param {string} plaintext - 明文
 * @param {string} publicKeyPem - PEM 格式公钥
 * @returns {Promise<string>} Base64 密文
 */
async function rsaEncrypt(plaintext, publicKeyPem) {
  const encrypt = new JSEncrypt();
  encrypt.setPublicKey(publicKeyPem);
  const encrypted = encrypt.encrypt(plaintext);
  if (!encrypted) {
    throw new Error('RSA 加密失败，请检查公钥格式或明文长度');
  }
  return encrypted;
}

// ==================== HMAC 密钥派生 ====================

/**
 * 从 userToken 派生对称密钥（HMAC-SHA256）
 * @param {string} userToken
 * @param {string} salt - 固定盐值
 * @returns {Promise<ArrayBuffer>} 32 字节密钥
 */
async function deriveKey(userToken, salt) {
  userToken = userToken.split('Bearer ')[1]
  const derivedWordArray = CryptoJS.HmacSHA256(salt, userToken);
  return wordArrayToArrayBuffer(derivedWordArray);
}

// ==================== HMAC-SHA256 签名 ====================

/**
 * HMAC-SHA256 签名
 * @param {ArrayBuffer} key - 密钥
 * @param {string} data - 待签名数据
 * @returns {Promise<string>} 十六进制小写签名
 */
async function hmacSha256(key, data) {
  const keyWordArray = arrayBufferToWordArray(key);
  const signature = CryptoJS.HmacSHA256(data, keyWordArray);
  return signature.toString(CryptoJS.enc.Hex);
}

/**
 * AES-GCM 解密
 * @param {string} combinedBase64 - Base64(IV + 密文 + GCM标签)
 * @param {ArrayBuffer} key - 32 字节密钥
 * @returns {string} 明文字符串
 */
async function aesGcmDecrypt(combinedBase64, key) {
  // base64 解码完整包：iv(12) + ciphertext + tag(16)
  const combinedBuf = base64ToArrayBuffer(combinedBase64);
  const combined = new Uint8Array(combinedBuf);

  const iv = combined.slice(0, 12);
  const ciphertextWithTag = combined.slice(12);
  const keyUint8 = new Uint8Array(key);

  // gcm(key, iv) 创建解密器，decrypt自动校验末尾16字节tag
  const dec = gcm(keyUint8, iv);
  const plainUint8 = dec.decrypt(ciphertextWithTag);
  
  return arrayBufferToStr(plainUint8.buffer);
}

// ==================== 导出 ====================

export {
  rsaEncrypt,
  deriveKey,
  hmacSha256,
  strToArrayBuffer,
  arrayBufferToStr,
  arrayBufferToBase64,
  base64ToArrayBuffer,
  aesGcmDecrypt
};
